import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import chroma from 'chroma-js';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';

// components
import Block from '../../stories/layout-components/Block';
import GeneralText from '../../stories/general-components/GeneralText';
import TextField from '../../stories/general-components/TextField';
import Select from '../../stories/general-components/Select';
import Checkbox from '../../stories/general-components/Checkbox';
import nodeAxiosFirebase from '../../utils/nodeAxiosFirebase';
import FieldEditor from '../../components/@generalComponents/FieldEditor';
import {
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Paper,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import WorkshopLayout from '../../layouts/WorkshopLayout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import getListStructureOptions from '../../utils/getListStructureOptions';
import getListModuleOptions from '../../utils/getListModuleOptions';

const SettingsCollectionsWS = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const businessPreference = useSelector((state) => state.core.businessData);
  const { collectionId } = useParams();
  const currentUser = useSelector((state) => state.core.user);
  const businessStructure = useSelector((state) => state.core.businessStructure);
  const mainColor = businessPreference?.mainColor || '#000000';
  const darkColor = chroma(mainColor).darken(0.99).hex();

  const [formData, setFormData] = useState({});
  const [expanded, setExpanded] = useState('primaryConfigs');
  const [loading, setLoading] = useState(true);
  const [structureOptions, setStructureOptions] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [fields, setFields] = useState([]);
  const [vinCheckEnabled, setVinCheckEnabled] = useState(false);
  const [vinApiFields, setVinApiFields] = useState([]);
  const [vinFieldMappings, setVinFieldMappings] = useState({});

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeIndex = parseInt(searchParams.get('tab')) || 0;

  // Handle accordion expand/collapse
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Load structure and module options based on collectionId
  useEffect(() => {
    const structureOpts = getListStructureOptions();
    const moduleOpts = getListModuleOptions();

    setStructureOptions(structureOpts);
    setModuleOptions(moduleOpts);

    // Get fields from the business structure
    const structure = businessStructure?.structures?.find(
      (s) => s.collectionField === collectionId
    );

    if (structure) {
      const formattedFields = structure.fields?.map(field => ({
        ...field,
        id: field.value || field.id,
        name: field.name || "",
        typeData: field.typeData || "string",
        type: field.type || "data",
        required: field.required || false,
        visible: field.visible !== false,
        transform: field.transform || null,
        validation: field.validation || null,
        public: field.public || false,
        selections: field.selections || []
      })) || [];

      setFields(formattedFields.filter(field => field.type !== 'default'));
    }

    // Initialize form data with default values
    setFormData({
      primaryConfigs: {
        displayName: true,
      },
      preferences: {}
    });

    // Check if VIN check is enabled (for 'profiles' collection)
    if (collectionId === 'profiles') {
      // Get VIN settings from the structure
      const vinSettings = structure?.element?.chekup?.preferences || {};

      // Set VIN check state
      setVinCheckEnabled(vinSettings.vinCheckEnabled || false);

      // Initialize VIN API fields
      initializeVinApiFields();

      // Set field mappings
      setVinFieldMappings(vinSettings.vinFieldMappings || {});

      // Update form data with VIN settings
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          vinCheckEnabled: vinSettings.vinCheckEnabled || false,
          vinField: vinSettings.vinField || '',
          vinProcedure: vinSettings.vinProcedure || 'displayOnly',
          vinFieldMappings: vinSettings.vinFieldMappings || {}
        }
      }));
    }

    setLoading(false);
  }, [collectionId, businessStructure, businessPreference]);


  // Fonction pour initialiser les champs de l'API VIN à partir des données d'exemple
  const initializeVinApiFields = () => {
    const apiFields = [
      { value: "Make", label: t('make'), example: "TOYOTA" },
      { value: "Model", label: t('model'), example: "Prius Prime (PHEV)" },
      { value: "Model Year", label: t('year'), example: "2020" },
      { value: "Vehicle Type", label: t("type"), example: "PASSENGER CAR" },
      { value: "Body Class", label: t('bodyClass'), example: "Sedan/Saloon" },
      { value: "Doors", label: t('doors'), example: "5" },
      { value: "Plant Country", label: t('plantCountry'), example: "JAPAN" },
      { value: "Manufacturer Name", label: t('manufacturerName'), example: "TOYOTA MOTOR CORPORATION" },
      { value: "Plant City", label: t('plantCity'), example: "TOYOTA CITY" },
      { value: "Series", label: t('series'), example: "ZVW52L-AHXEBA / ZVW52L-AHXGBA" },
      { value: "Trim", label: t('trim'), example: "" },
      { value: "Drive Type", label: t('driveType'), example: "4x2" },
      { value: "Number of Seats", label: t('numberOfSeats'), example: "5" },
      { value: "Transmission Style", label: t('transmissionStyle'), example: "Electronic Continuously Variable (e-CVT)" },
      { value: "Engine Number of Cylinders", label: t('engineNumberOfCylinders'), example: "4" },
      { value: "Displacement (L)", label: t('displacement'), example: "1.8" },
      { value: "Fuel Type - Primary", label: t('fuelTypePrimary'), example: "Electric" },
      { value: "Fuel Type - Secondary", label: t('fuelTypeSecondary'), example: "Gasoline" },
      { value: "Engine Model", label: t('engineModel'), example: "2ZR-FXE+1NM+1SM" },
      { value: "Engine Brake (hp) From", label: t('engineBrakeHpFrom'), example: "96" },
      { value: "Steering Location", label: t('steeringLocation'), example: "Left-Hand Drive (LHD)" },
      { value: "Electrification Level", label: t('electrificationLevel'), example: "PHEV (Plug-in Hybrid Electric Vehicle)" }
    ];

    setVinApiFields(apiFields);
  };

  // Handle form field changes
  const handleFieldChange = (section, field, value) => {
    setFormData(prevData => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: value
      }
    }));
  };

  // Handle VIN check toggle
  const handleVinCheckToggle = (checked) => {
    setVinCheckEnabled(checked);
    handleFieldChange('preferences', 'vinCheckEnabled', checked);

    // Si désactivé, réinitialiser les champs liés au VIN
    if (!checked) {
      const updatedPreferences = { ...formData.preferences };
      delete updatedPreferences.vinField;
      delete updatedPreferences.vinProcedure;
      delete updatedPreferences.vinFieldsToFill;

      setFormData(prevData => ({
        ...prevData,
        preferences: updatedPreferences
      }));
    }
  };



  const handleSavePreferences = async () => {
    try {
      setLoading(true);


      // Ensure VIN mappings are included in the preferences if VIN check is enabled
      let preferencesData = { ...formData.preferences };
      if (collectionId === 'profiles' && vinCheckEnabled) {
        preferencesData = {
          ...preferencesData,
          vinCheckEnabled,
          vinFieldMappings: vinFieldMappings
        };
      }

      const updatedFormData = {
        ...formData,
        ...preferencesData,
      };

      await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/structure`,
        body: {
          collectionField: collectionId,
          brand: 'CHEKUP',
          brandData: updatedFormData,
          type: "general"
        },
      });
      toast.success(t('saved'));
      setLoading(false);
    } catch (error) {
      console.error('Error saving collection preferences', error);
      setLoading(false);
    }
  };

  // Add to browsing history
  useEffect(() => {
    const currentHistoryToAdd = {
      pathname: location.pathname,
      url: location.pathname + location.search,
      name: t('settings'),
      moduleName: t('settings'),
      structureId: '',
      businessId: businessPreference?.id,
      timeStamp: moment().format('HH:mm:ss'),
    };

    let existingHistory = JSON.parse(localStorage.getItem('history'));

    if (!Array.isArray(existingHistory)) {
      existingHistory = [];
    }

    // Remove any existing history with the same pathname
    existingHistory = existingHistory.filter(
      (history) => history.pathname !== location.pathname
    );

    const newHistory = [...existingHistory, currentHistoryToAdd];
    if (newHistory.length > 15) {
      newHistory.shift();
    }

    localStorage.setItem('history', JSON.stringify(newHistory));
  }, [location.pathname]);

  // Helper function to render common actions
  const renderCommonActions = () => {
    return (
      <Grid container spacing={2}>


      </Grid>
    );
  };

  // Filter out fields with typeData === 'default' for FieldEditor
  const filteredFields = fields?.filter(field => field?.type !== 'default');

  // Obtenir la structure complète de la collection actuelle
  const currentStructure = businessStructure?.structures?.find(s => s.collectionField === collectionId);

  // Générer les options de champs pour le sélecteur de champ VIN à partir de TOUS les champs
  // Ajouter le type de champ dans le libellé pour aider l'utilisateur
  const fieldOptions = currentStructure?.fields
    ?.map(field => ({
      label: `${field.name} (${field.typeData || 'unknown'})`,
      value: field.value,
      id: field.value
    }))
    ?.sort((a, b) => a.label.localeCompare(b.label)) || [];

  // Function to handle field mapping changes
  const handleFieldMappingChange = (businessFieldId, apiFieldIds) => {
    const updatedMappings = { ...vinFieldMappings };

    if (apiFieldIds.length === 0) {
      // If no API fields are selected, remove the mapping
      delete updatedMappings[businessFieldId];
    } else {
      // Update mapping with the selected API fields
      updatedMappings[businessFieldId] = apiFieldIds;
    }

    setVinFieldMappings(updatedMappings);

    // Update form data
    handleFieldChange('preferences', 'vinFieldMappings', updatedMappings);
  };

  // Function to toggle a single API field for a business field
  const toggleApiField = (businessFieldId, apiFieldId) => {
    const currentMappings = vinFieldMappings[businessFieldId] || [];
    let newMappings;

    if (currentMappings.includes(apiFieldId)) {
      // Remove API field if already mapped
      newMappings = currentMappings.filter(id => id !== apiFieldId);
    } else {
      // Add API field to mapping
      newMappings = [...currentMappings, apiFieldId];
    }

    handleFieldMappingChange(businessFieldId, newMappings);
  };

  // Function to get mapped API fields for a business field
  const getMappedApiFields = (businessFieldId) => {
    return vinFieldMappings[businessFieldId] || [];
  };

  // Composant pour le mappage d'un champ business avec des champs API
  const MappingField = ({ businessField, apiFields, mappedApiFieldIds, onToggleApiField }) => {
    const [menuAnchor, setMenuAnchor] = useState(null);

    const handleOpenMenu = (event) => {
      setMenuAnchor(event.currentTarget);
    };

    const handleCloseMenu = () => {
      setMenuAnchor(null);
    };

    return (
      <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="subtitle2">{businessField.name}</Typography>
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {mappedApiFieldIds.length > 0 ? (
            mappedApiFieldIds.map(apiFieldId => {
              const apiField = apiFields.find(f => f.value === apiFieldId);
              return (
                <Chip
                  key={apiFieldId}
                  label={apiField?.label || apiFieldId}
                  onDelete={() => onToggleApiField(businessField.value, apiFieldId)}
                  color="primary"
                  size="small"
                />
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('noFieldsMapped')}
            </Typography>
          )}
          <Chip
            icon={<ExpandMoreIcon />}
            label={t('addField')}
            onClick={handleOpenMenu}
            variant="outlined"
            size="small"
          />
        </Box>

        <Box component="div" sx={{ mt: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1,
              display: mappedApiFieldIds.length > 0 ? 'block' : 'none'
            }}
          >
            <Typography variant="caption">
              {t('fieldPreview')}: {
                mappedApiFieldIds.map(apiFieldId => {
                  const apiField = apiFields.find(f => f.value === apiFieldId);
                  return apiField?.example || '';
                }).filter(Boolean).join(' - ')
              }
            </Typography>
          </Paper>
        </Box>

        {/* Menu de sélection des champs API */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
          sx={{ maxHeight: 300 }}
        >
          {apiFields.map(apiField => (
            <MenuItem
              key={apiField.value}
              onClick={() => {
                onToggleApiField(businessField.value, apiField.value);
                // Ne pas fermer le menu pour permettre plusieurs sélections
              }}
              sx={{
                backgroundColor: mappedApiFieldIds.includes(apiField.value) ? 'action.selected' : 'transparent'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">
                  {mappedApiFieldIds.includes(apiField.value) && '✓ '}
                  {apiField.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {apiField.example}
                </Typography>
              </Box>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem onClick={handleCloseMenu}>
            <Typography variant="body2" sx={{ width: '100%', textAlign: 'center' }}>
              {t('done')}
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <WorkshopLayout
      icon="SettingsOutlined"
      pageTitle={t(collectionId)}
      subTitle={t('collectionSettings')}
      actions={{
        save: () => {
          handleSavePreferences();
        },
      }}
      selectedTab={activeIndex}
    >
      <Block height={1} heightPercentage={98} noBorder>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <GeneralText text={t('loading')} />
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {/* Primary Configurations */}
            <Accordion
              elevation={0}
              expanded={expanded === 'primaryConfigs'}
              onChange={handleAccordionChange('primaryConfigs')}
              sx={{ mb: 2, backgroundColor: 'transparent' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{t('primaryConfigurations')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderCommonActions()}
              </AccordionDetails>
            </Accordion>

            {/* Fields Section */}
            <Accordion
              elevation={0}
              expanded={expanded === 'fields'}
              onChange={handleAccordionChange('fields')}
              sx={{ mb: 2, backgroundColor: 'transparent' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{t('fields')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* 
                  FieldEditor est un composant avancé qui permet de gérer les champs
                  Il prend en charge l'édition complète des champs avec leurs propriétés,
                  validations, sélections, et autres options avancées.
                */}
                <FieldEditor
                  fields={filteredFields}
                  setFields={(newFields) => {
                    // Mise à jour des champs dans l'état local
                    setFields(newFields);

                    // Préparation des données pour la sauvegarde
                    const fieldsToSave = {};
                    newFields.forEach(field => {
                      if (field.value) {
                        fieldsToSave[field.value] = {
                          required: field.required || false,
                          visible: field.visible !== false,
                          searchable: field.searchable || false,
                          validation: field.validation || null,
                          transform: field.transform || null,
                          typeData: field.typeData,
                          name: field.name,
                          public: field.public || false,
                          selections: field.selections || []
                        };
                      }
                    });

                    // Mise à jour du formData avec les nouveaux champs
                    setFormData(prevData => ({
                      ...prevData,
                      fields: fieldsToSave
                    }));
                  }}
                />
              </AccordionDetails>
            </Accordion>

            {/* Collection Specific Preferences */}
            <Accordion
              elevation={0}
              sx={{ backgroundColor: 'transparent' }}
              expanded={expanded === 'preferences'}
              onChange={handleAccordionChange('preferences')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{t('preferences')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Collection-specific preferences */}
                  {collectionId === 'cardsuninvoiced' && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <Select
                          select
                          staticView
                          noEmpty
                          label={t('approvalFlow')}
                          value={formData.preferences?.approvalFlow || 'no'}
                          selections={[
                            { label: t('yes'), value: 'yes', id: 'yes' },
                            { label: t('no'), value: 'no', id: 'no' },
                          ]}
                          onChange={(e, value) => handleFieldChange('preferences', 'approvalFlow', value)}
                          fullWidth
                        />
                      </Grid>
                    </>
                  )}

                  {collectionId === 'cardsinvoiced' && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <Select
                          select
                          staticView
                          noEmpty
                          label={t('allowPublicPayment')}
                          value={formData.preferences?.allowPublicPayment || 'no'}
                          selections={[
                            { label: t('yes'), value: 'yes', id: 'yes' },
                            { label: t('no'), value: 'no', id: 'no' },
                          ]}
                          onChange={(e, value) => handleFieldChange('preferences', 'allowPublicPayment', value)}
                          fullWidth
                        />
                      </Grid>
                    </>
                  )}

                  {collectionId === 'passes' && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <Select
                          select
                          staticView
                          noEmpty
                          label={t('confirmationPresenceEmail')}
                          value={formData.preferences?.confirmationPresenceEmail || 'notAtAll'}
                          selections={[
                            { label: t('notAtAll'), value: 'notAtAll', id: 'notAtAll' },
                            { label: t('yes24h'), value: 'yes24h', id: 'yes24h' },
                            { label: t('yes48h'), value: 'yes48h', id: 'yes48h' },
                            { label: t('yes72h'), value: 'yes72h', id: 'yes72h' },
                          ]}
                          onChange={(e, value) => handleFieldChange('preferences', 'confirmationPresenceEmail', value)}
                          fullWidth
                        />
                      </Grid>
                    </>
                  )}

                  {/* VIN Check option pour collection Profiles */}
                  {collectionId === 'profiles' && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <Checkbox
                          label={t('vinCheck')}
                          value={vinCheckEnabled}
                          onChange={(e) => handleVinCheckToggle(e.target.checked)}
                          fullWidth
                        />
                      </Grid>

                      {/* Si VIN Check est activé, afficher le sélecteur de champ VIN */}
                      {vinCheckEnabled && (
                        <>
                          <Grid item xs={12} sm={6} md={4}>
                            <Select
                              select
                              staticView
                              noEmpty
                              label={t('vinField')}
                              value={formData.preferences?.vinField || ''}
                              selections={fieldOptions}
                              onChange={(e, value) => handleFieldChange('preferences', 'vinField', value)}
                              fullWidth
                              help={t('selectFieldContainingVIN')}
                            />
                          </Grid>

                          {/* Afficher les options de procédure si un champ VIN est sélectionné */}
                          {formData.preferences?.vinField && (
                            <>
                              <Grid item xs={12} sm={6} md={4}>
                                <Select
                                  select
                                  staticView
                                  noEmpty
                                  label={t('vinProcedure')}
                                  value={formData.preferences?.vinProcedure || 'displayOnly'}
                                  selections={[
                                    { label: t('overwriteExistingData'), value: 'overwrite', id: 'overwrite' },
                                    { label: t('mergeExistingData'), value: 'merge', id: 'merge' },
                                    { label: t('displayDataOnly'), value: 'displayOnly', id: 'displayOnly' }
                                  ]}
                                  onChange={(e, value) => handleFieldChange('preferences', 'vinProcedure', value)}
                                  fullWidth
                                  help={t('selectVinProcedure')}
                                />
                              </Grid>


                            </>
                          )}
                        </>
                      )}

                      {/* Section de mappage des champs VIN si un champ VIN est sélectionné */}
                      {vinCheckEnabled && formData.preferences?.vinField && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                              {t('vinDataMapping')}
                            </Typography>

                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ mt: 2 }}>
                              {/* Filtrer les champs qui peuvent être utilisés pour le mappage (texte, nombre, date, etc.) */}
                              {currentStructure?.fields
                                ?.filter(field =>
                                  field.value !== formData.preferences?.vinField && // Exclure le champ VIN lui-même
                                  field.typeData !== 'default' &&
                                  !['media', 'media-single', 'media-single-private'].includes(field.typeData)
                                )
                                ?.map(businessField => (
                                  <MappingField
                                    key={businessField.value}
                                    businessField={businessField}
                                    apiFields={vinApiFields}
                                    mappedApiFieldIds={getMappedApiFields(businessField.value)}
                                    onToggleApiField={toggleApiField}
                                  />
                                ))
                              }
                            </Box>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Block>
    </WorkshopLayout>
  );
};

export default SettingsCollectionsWS;
